import AdminUserLoginChecker from '@/components/AdminUserLoginChecker';
import DevelopmentEnvironmentBanner from '@/components/DevelopmentEnvironmentBanner';
import ToastNotification from '@/components/ToastNotification';
import client from '@/graphql/client';
import { useAppSelector } from '@/hooks/store';
import { FC, lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Provider as UrqlProvider } from 'urql';
import ScrollToTop from './components/ScrollToTop';
import GlobalStyles from './GlobalStyles';

const Index = lazy(() => import('@/pages/Index'));
const CreateItem = lazy(() => import('@/pages/CreateItem'));
const Edit = lazy(() => import('@/pages/EditItem'));
const Discontinue = lazy(() => import('@/pages/Discontinue'));
const ReadOnlyItem = lazy(() => import('@/pages/ReadOnlyItem'));
const ItemSubstitutes = lazy(() => import('@/pages/ItemSubstitutes'));
const CreateBaseBrandedItem = lazy(
  () => import('@/pages/CreateBaseBrandedItem'),
);
const EditBaseBrandedItem = lazy(() => import('@/pages/EditBaseBrandedItem'));
const ReadOnlyBaseBrandedItem = lazy(
  () => import('@/pages/ReadOnlyBaseBrandedItem'),
);
const ItemIssuesIndex = lazy(() => import('@/pages/ItemIssuesIndex'));
const NewItemsPinningPage = lazy(() => import('@/pages/NewItemsPinningPage'));

import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { NEW_ITEMS_PINNING_PATH } from './components/Collection/NewItemsCollectionCallout';
import AddCollectionToHomepage from './pages/AddCollectionToHomepage';
import BaseBrandedItemIndex from './pages/BaseBrandedItemIndex';
import BrandedItemLeadTime from './pages/BrandedItemLeadTime';
import BrandManagerIndex from './pages/BrandManagerIndex';
import CategoriesIndex from './pages/CategoriesIndex';
import CategoryBannerEditor from './pages/CategoryBannerEditor';
import CollectionsIndex from './pages/CollectionsIndex';
import CreateCollectionPromoPage from './pages/CreateCollectionPromoPage';
import LocationItemManager, {
  LocationItemManagerUrl,
} from './pages/LocationItemManager';

/**
 * Initialize our GraphQL Client.
 * Set the env var GRAPHQL_ENDPOINT to use an endpoint other than the backend's
 * (in the item-management-service repo)
 *
 * NOTE: devtoolsExchange should be the first exchange according to the docs
 * https://github.com/FormidableLabs/urql-devtools-exchange#usage
 */

const App: FC = () => {
  const urlqConfig = client(useAppSelector((state) => state.auth.token)!);
  return (
    <>
      <GlobalStyles />
      <AdminUserLoginChecker>
        <UrqlProvider value={urlqConfig}>
          <BrowserRouter>
            <Suspense fallback={<div>Loading...</div>}>
              <ScrollToTop />
              <DevelopmentEnvironmentBanner />
              <ToastNotification />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/discontinue/:id" element={<Discontinue />} />
                <Route path="/edit/:id" element={<Edit />} />
                <Route path="/item/:id" element={<ReadOnlyItem />} />
                <Route
                  path="/item/:id/substitutes"
                  element={<ItemSubstitutes />}
                />
                <Route path="/new/:type" element={<CreateItem />} />
                <Route
                  path={LocationItemManagerUrl}
                  element={<LocationItemManager />}
                />
                <Route
                  path={`${LocationItemManagerUrl}/:id`}
                  element={<LocationItemManager />}
                />
                <Route
                  path="/base_branded_items"
                  element={<BaseBrandedItemIndex />}
                />
                <Route
                  path="/base_branded_items/lead_times"
                  element={<BrandedItemLeadTime />}
                />
                <Route
                  path="/base_branded_item/new/:type"
                  element={<CreateBaseBrandedItem />}
                />
                <Route
                  path="/base_branded_item/edit/:id"
                  element={<EditBaseBrandedItem />}
                />
                <Route
                  path="/base_branded_item/item/:id"
                  element={<ReadOnlyBaseBrandedItem />}
                />
                <Route path="/brands" element={<BrandManagerIndex />} />
                <Route path="/categories" element={<CategoriesIndex />} />
                <Route
                  path="/categories/:categoryId"
                  element={<CategoryBannerEditor />}
                />
                <Route path="/collections" element={<CollectionsIndex />} />
                <Route
                  path="/collections/add-to-homepage"
                  element={<AddCollectionToHomepage />}
                />
                <Route
                  path="/collections/new"
                  element={<CreateCollectionPromoPage />}
                />
                <Route
                  path="/collections/:id"
                  element={<CreateCollectionPromoPage />}
                />
                <Route path="/item-issues" element={<ItemIssuesIndex />} />
                <Route
                  path={NEW_ITEMS_PINNING_PATH}
                  element={<NewItemsPinningPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </UrqlProvider>
      </AdminUserLoginChecker>
    </>
  );
};

export default !window.env.LAUNCH_DARKLY_SDK_KEY
  ? App
  : (withLDProvider({
      clientSideID: window.env.LAUNCH_DARKLY_SDK_KEY,
      options: {
        bootstrap: 'localStorage',
        streaming: true,
      },
    })(App) as FC);
